import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { Helmet } from "react-helmet";

const Login = () => {
  const navigate = useNavigate();
  const { isLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="w-16 h-16 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");

    try {
      let userCredential;

      if (isRegistering) {
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }
        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          return;
        }
        userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
      } else {
        userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
      }
    } catch (error) {
      console.error("Authentication failed:", error);
      switch (error.code) {
        case "auth/email-already-in-use":
          setError(
            "An account with this email already exists. Please log in instead."
          );
          break;
        case "auth/user-not-found":
          setError("No account found with this email. Please register first.");
          break;
        case "auth/wrong-password":
          setError("Incorrect password. Please try again.");
          break;
        default:
          setError("Authentication failed. Please try again.");
      }
    }
  };

  const googleProvider = new GoogleAuthProvider();
  const GoogleLogin = async () => {
    try {
      // Kill any existing popup first
      if (auth.currentUser) {
        await auth.signOut();
      }

      const googleProvider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, googleProvider);
      console.log("Success!");
    } catch (error) {
      console.error("Error:", error.code);
      setError(`Login failed: ${error.code}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Recovery House Platform &bull; {isRegistering ? "Register" : "Login"}
        </title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="relative min-h-screen flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="flex gap-1">
                <div className="h-8 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
                <div className="h-8 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
                <div className="h-8 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
              </div>
              <span className="text-white font-bold text-xl">
                Recovery House Platform
              </span>
            </div>

            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-800 overflow-hidden">
              <div className="p-8">
                <h1 className="text-2xl font-bold text-white text-center mb-8">
                  {isRegistering ? "Create Account" : "Welcome Back"}
                </h1>

                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleAuth} className="space-y-6">
                  <div className="relative">
                    <input
                      required
                      type="email"
                      value={email}
                      placeholder="Email"
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400 transition-all duration-200"
                    />
                  </div>

                  <div className="relative">
                    <input
                      required
                      type="password"
                      value={password}
                      placeholder="Password"
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400 transition-all duration-200"
                    />
                  </div>

                  {isRegistering && (
                    <div className="relative">
                      <input
                        required
                        type="password"
                        value={confirmPassword}
                        placeholder="Confirm Password"
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-white placeholder-slate-400 transition-all duration-200"
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-lg transition-all duration-300 font-medium"
                  >
                    {isRegistering ? "Create Account" : "Sign in"}
                  </button>
                </form>

                <button
                  onClick={() => setIsRegistering(!isRegistering)}
                  className="w-full mt-4 text-sm text-slate-400 hover:text-white transition-colors duration-200"
                >
                  {isRegistering
                    ? "Already have an account? Sign in"
                    : "Don't have an account? Create one"}
                </button>

                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-700"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 text-sm text-slate-400 bg-slate-900/50">
                      OR
                    </span>
                  </div>
                </div>

                <button
                  onClick={GoogleLogin}
                  className="w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 rounded-lg transition-all duration-300 flex items-center justify-center gap-3 border border-slate-700"
                >
                  <span className="text-white font-medium">
                    Sign in with Google
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
